import { m as motion } from "framer-motion";
import { CurriculumSection } from "@/components/curriculum-section";
import { CallToActionSection } from "@/components/call-to-action-section";
import { Cpu, FileText, Brain, ArrowRight, Sparkles, Clock, Users, GraduationCap, Monitor } from "lucide-react";
import { Link } from "react-router-dom";
import { EXTERNAL_LINKS } from "@/lib/constants";
import { NEXT_COHORT_APPLY_CTA, NEXT_COHORT_PATH, NEXT_COHORT_TIME_LABEL } from "@/lib/cohorts";
import { useSEO } from "@/lib/use-seo";
import { breadcrumb } from "@/lib/seo-schemas";

const outcomes = [
  {
    icon: <Cpu className="w-8 h-8 text-primary" />,
    title: "A Working AI Prototype",
    description:
      "Walk away with a real proof-of-concept built for your specific HR/Talent workflow.",
  },
  {
    icon: <FileText className="w-8 h-8 text-secondary" />,
    title: "A Scaling Action Plan",
    description:
      "A concrete roadmap to take your prototype from pilot to organization-wide deployment.",
  },
  {
    icon: <Brain className="w-8 h-8 text-accent" />,
    title: "Behavioral Science Frameworks",
    description:
      "Evidence-based strategies to drive adoption and make your AI initiatives stick.",
  },
];

const Curriculum = () => {
  useSEO({
    title: "Curriculum — 5-Session AI for HR Program",
    description: "From AI readiness assessment to a scaled action plan. Explore the 5-session curriculum covering use case selection, prototyping, knowledge preparation, and scaling.",
    path: "/curriculum",
    ogImage: "/images/og-curriculum.jpg",
    jsonLd: breadcrumb([
      { name: "Home", path: "/" },
      { name: "Curriculum", path: "/curriculum" },
    ]),
  });
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-mesh pt-36 md:pt-32 pb-8">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-radial-primary opacity-30 blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-radial-secondary opacity-30 blur-3xl" />
        </div>
        <div className="section-inner relative z-10 text-center space-y-5">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold"
          >
            Your{" "}
            <span className="bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))] bg-clip-text text-transparent">
              5-Week Journey
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto"
          >
            From assessing your AI readiness to deploying a working prototype
            — each session builds on the last so you leave with real, measurable results.
          </motion.p>
        </div>
      </section>

      {/* Format & Logistics */}
      <section className="container-class relative overflow-hidden">
        <div className="section-inner">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            className="text-3xl md:text-4xl font-bold text-center mb-10"
          >
            How It Works
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Clock className="w-6 h-6 text-primary" />,
                label: "Duration",
                value: "5 weeks, 1 session/week",
                detail: "90 min live sessions",
              },
              {
                icon: <Monitor className="w-6 h-6 text-primary" />,
                label: "Format",
                value: "Live & interactive",
                detail: `Thursdays at ${NEXT_COHORT_TIME_LABEL}`,
              },
              {
                icon: <Users className="w-6 h-6 text-primary" />,
                label: "Who It's For",
                value: "HR & Talent leaders",
                detail: "No technical background needed",
              },
              {
                icon: <GraduationCap className="w-6 h-6 text-primary" />,
                label: "What You Get",
                value: "Prototype + action plan",
                detail: "Plus lifetime community access",
              },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.08 }}
                className="glass-card p-6 text-center space-y-2"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mb-1">
                  {item.icon}
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-primary/70">
                  {item.label}
                </p>
                <p className="text-lg font-bold">{item.value}</p>
                <p className="text-sm text-muted-foreground">{item.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sessions timeline */}
      <CurriculumSection />

      {/* Outcomes */}
      <section className="container-class relative overflow-hidden">
        <div className="section-inner">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            className="text-3xl md:text-4xl font-bold text-center mb-12"
          >
            What You'll Walk Away With
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {outcomes.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.1 }}
                className="glass-card p-8 text-center hover-lift"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Callout */}
      <section className="container-class relative overflow-hidden">
        <div className="section-inner">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="glass-card p-10 md:p-14 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-gradient-radial-primary opacity-10 blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-gradient-radial-secondary opacity-10 blur-3xl pointer-events-none" />

            <div className="relative z-10 space-y-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary/10 border-2 border-primary/20">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>

              <h2 className="text-3xl md:text-4xl font-bold">
                Invest in Your AI Fluency
              </h2>

              <div className="flex flex-col items-center gap-1">
                <span className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--secondary))] bg-clip-text text-transparent">
                  $1,400
                </span>
                <span className="text-muted-foreground text-lg">
                  Lifetime Access + Live Cohorts
                </span>
                <span className="text-sm text-muted-foreground/70">
                  Limited to 200 members
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
                <a
                  href={EXTERNAL_LINKS.PREMIUM_MASTERMIND_APPLICATION}
                  className="group inline-flex items-center gap-2 px-8 py-4 text-lg font-semibold rounded-2xl bg-primary text-primary-foreground shadow-primary hover:shadow-glow-primary transition-all duration-300 hover:-translate-y-1"
                >
                  {NEXT_COHORT_APPLY_CTA}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </a>
                <Link
                  to={NEXT_COHORT_PATH}
                  className="text-foreground/70 hover:text-primary transition-colors underline underline-offset-4"
                >
                  Learn about the next cohort →
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <CallToActionSection />
    </div>
  );
};

export default Curriculum;
